import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Input, InputNumber, Segmented, Select, Space, Typography } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { adminApi } from '@/services/api';
import type { PvpSyncPlatform } from '@/types/pvp';
import { SyncReport } from './SyncReport';
import {
  APP_MAX,
  INT_MAX,
  DEFAULT_PRESET,
  PLATFORMS,
  SYNC_PRESETS,
  createSyncLoader,
  type SyncFilters,
  type SyncPreset,
  type SyncState,
} from './model';

const { Title, Text } = Typography;

/**
 * Realtime PvP (Rivals) sync: whether the two phones of a match stay in step,
 * merged from the per-match sync rows over a time range. Choosing a range or
 * pressing Refresh reads the newest numbers; the filters apply with them.
 */
export const PvpSyncPage: React.FC = () => {
  const [preset, setPreset] = useState<SyncPreset>(DEFAULT_PRESET);
  const [filters, setFilters] = useState<SyncFilters>({});
  const [state, setState] = useState<SyncState>({ data: null, loading: false, error: null });
  const loader = useMemo(
    () =>
      createSyncLoader(adminApi.pvp.sync, (patch) =>
        setState((current) => ({ ...current, ...patch }))
      ),
    []
  );

  const refresh = useCallback(
    (nextPreset: SyncPreset = preset) => loader.load(nextPreset, filters, new Date()),
    [loader, preset, filters]
  );

  // First load only: later reads come from a chosen range or Refresh.
  useEffect(() => {
    loader.load(DEFAULT_PRESET, {}, new Date());
  }, [loader]);

  const setFilter = <K extends keyof SyncFilters>(key: K, value: SyncFilters[K]) =>
    setFilters((current) => ({ ...current, [key]: value }));

  return (
    <div>
      <Title level={3} style={{ marginBottom: 0 }}>
        PvP Sync
      </Title>
      <Text type="secondary">
        Whether the two phones of Realtime PvP (Rivals) matches stay in step: ball start, the
        batter's swing reaching the watcher, clock and stalls.
      </Text>

      <Space wrap style={{ margin: '12px 0', width: '100%', justifyContent: 'space-between' }}>
        <Segmented
          options={SYNC_PRESETS.map(({ label, value }) => ({ label, value }))}
          value={preset}
          onChange={(v) => {
            const next = v as SyncPreset;
            setPreset(next);
            refresh(next);
          }}
        />
        <Button icon={<ReloadOutlined />} onClick={() => refresh()} loading={state.loading}>
          Refresh
        </Button>
      </Space>

      <Space wrap style={{ marginBottom: 4 }}>
        <InputNumber
          aria-label="Sim version"
          placeholder="Sim version"
          min={0}
          max={INT_MAX}
          precision={0}
          value={filters.simVersion ?? null}
          onChange={(v) => setFilter('simVersion', v)}
          onPressEnter={() => refresh()}
          style={{ width: 130 }}
        />
        <Input
          aria-label="App"
          placeholder="App (one phone)"
          maxLength={APP_MAX}
          allowClear
          value={filters.app ?? ''}
          onChange={(e) => setFilter('app', e.target.value)}
          onPressEnter={() => refresh()}
          style={{ width: 170 }}
        />
        <InputNumber
          aria-label="Build"
          placeholder="Build (bv)"
          min={0}
          max={INT_MAX}
          precision={0}
          value={filters.bv ?? null}
          onChange={(v) => setFilter('bv', v)}
          onPressEnter={() => refresh()}
          style={{ width: 130 }}
        />
        <Select<PvpSyncPlatform | 'any'>
          aria-label="Platform"
          value={filters.platform ?? 'any'}
          onChange={(v) => setFilter('platform', v === 'any' ? undefined : v)}
          options={[
            { value: 'any', label: 'Any platform' },
            ...PLATFORMS.map((value) => ({ value, label: value })),
          ]}
          style={{ width: 150 }}
        />
      </Space>
      <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 12 }}>
        Filters apply when you choose a range or press Refresh. App, build and platform must all
        match the same phone.
      </Text>

      <SyncReport state={state} />
    </div>
  );
};

export default PvpSyncPage;
